import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, FileText } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { displayEmail, isPlaceholderEmail } from '../../lib/contactEmail';
import ConfirmDialog from '../shared/ConfirmDialog';

// Cases — one row per matter. The person (contact) is a column on the row;
// everything about the injury, its status and its funding lives here.
const CASE_PILL = {
  intake:      ['bg-accent-glow text-accent-light', 'Intake'],
  treatment:   ['bg-accent-glow text-accent-light', 'In treatment'],
  demand:      ['bg-warning-soft text-warning', 'Demand'],
  negotiation: ['bg-warning-soft text-warning', 'Negotiation'],
  litigation:  ['bg-danger-soft text-danger', 'In litigation'],
  settled:     ['bg-success-soft text-success', 'Settled'],
  closed:      ['bg-tertiary text-muted-c', 'Closed'],
};
const TYPE_CHIP = {
  auto_mva: ['MVA', 'bg-accent-glow text-accent-light'],
  premises: ['Premises', 'bg-accent-glow text-accent-light'],
  other:    ['PI', 'bg-tertiary text-muted-c'],
};
const TREATMENT_LABEL = { active: 'Active treatment', mmi: 'MMI', discharged: 'Discharged', referred: 'Referred out' };
const fmtD = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—');

export default function CasesPage() {
  const { currentOrg } = useOrg();
  const [cases, setCases] = useState([]);
  const [contactById, setContactById] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('open'); // open | closed
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const [list, contacts] = await Promise.all([api.listCases(currentOrg.id), api.getContacts(currentOrg.id)]);
      setCases(list || []);
      const m = {};
      for (const c of contacts || []) m[c.id] = c;
      setContactById(m);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => cases.map((cs) => {
    const p = contactById[cs.client_id] || {};
    const first = cs.client?.first_name || p.first_name || '';
    const last = cs.client?.last_name || p.last_name || '';
    return { ...cs, person: p, fullName: `${first} ${last}`.trim() || 'Client', initials: `${(first || '?')[0]}${(last || '?')[0]}`.toUpperCase() };
  }), [cases, contactById]);

  const filtered = rows.filter((r) => {
    const closed = ['settled', 'closed'].includes(r.status);
    if (view === 'open' ? closed : !closed) return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return r.fullName.toLowerCase().includes(t) || (r.case_number || '').toLowerCase().includes(t) || (r.person.email || '').toLowerCase().includes(t);
  });

  const handleInvite = async (r) => {
    setInviting(r.id);
    try {
      await api.inviteContact(currentOrg.id, r.client_id);
      setInviteSuccess(r.id);
      setTimeout(() => setInviteSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteCase(currentOrg.id, confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const findHref = (r) => `/app/referrals/concierge?client=${r.client_id}&return=${encodeURIComponent('/app/cases')}`;

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[1120px] mx-auto px-4 md:px-7 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c">Cases</h1>
            <p className="text-[12.5px] text-muted-c mt-0.5">
              Every matter and where it stands — PIP, MVA, premises, or any PI case. One client can have more than one.
            </p>
          </div>
          <Link
            to="/app/cases/new"
            className="flex items-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all whitespace-nowrap"
          >
            <Sparkles size={14} /> New case
          </Link>
        </div>

        {error && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">{error}</div>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex gap-0.5 bg-tertiary p-1 rounded-[10px] shrink-0">
            {[['open', 'Open cases'], ['closed', 'Closed']].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3.5 py-[7px] rounded-[7px] text-[12px] font-semibold transition-all ${view === v ? 'bg-accent text-white shadow-glow' : 'text-muted-c hover:text-primary-c'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <input type="text" className={inputClass} placeholder="Search by client or case number…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center px-5 py-3.5 border-b border-theme gap-5">
                <div className="w-[34px] h-[34px] rounded-[10px] bg-tertiary animate-pulse flex-shrink-0" />
                <div className="h-3.5 w-40 bg-tertiary rounded animate-pulse" />
                <div className="h-3 w-24 bg-tertiary rounded animate-pulse hidden md:block ml-auto" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-elevated rounded-xl border border-theme px-5 py-12 text-center">
            <p className="text-[14px] text-muted-c">
              {search ? 'No cases match your search.' : view === 'closed' ? 'No closed cases yet.' : 'No open cases yet. Start one with New case — upload the documents and the case fills itself.'}
            </p>
          </div>
        ) : (
          <>
            {/* Phones: stacked case cards — no sideways scrolling. */}
            <div className="md:hidden space-y-3">
              {filtered.map((r) => {
                const pill = CASE_PILL[r.status] || ['bg-tertiary text-muted-c', 'New'];
                const [typeLabel, typeCls] = TYPE_CHIP[r.case_type] || TYPE_CHIP.other;
                const alert = r.funding?.alerts?.[0];
                return (
                  <div key={r.id} className="bg-elevated rounded-xl border border-theme p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Link to={`/app/cases/${r.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-[38px] h-[38px] rounded-[10px] bg-accent-glow flex items-center justify-center text-[12px] font-bold text-accent-light flex-shrink-0">{r.initials}</div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-primary-c truncate">{r.fullName}</div>
                          <div className="text-[11px] text-muted-c">
                            <span className="font-mono">{r.case_number}</span> · Injury {fmtD(r.accident_date)}
                          </div>
                        </div>
                      </Link>
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10.5px] font-bold shrink-0 ${typeCls}`}>{typeLabel}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${pill[0]}`}>{pill[1]}</span>
                      {r.funding?.current?.chip && <span className="text-[11px] font-semibold text-secondary-c">{r.funding.current.chip}</span>}
                      {alert && <span className={`text-[10.5px] font-medium ${alert.level === 'critical' ? 'text-danger' : 'text-warning'}`}>{alert.text}</span>}
                      {r.treatment?.alerts?.[0] && <span className={`text-[10.5px] font-medium ${r.treatment.alerts[0].level === 'critical' ? 'text-danger' : 'text-warning'}`}>{r.treatment.alerts[0].text}</span>}
                      {r.person.new_docs_7d > 0 && (
                        <span className="flex items-center gap-1 text-[10.5px] font-medium text-accent-light"><FileText size={10} /> New document{r.person.new_docs_7d > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Link to={findHref(r)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-[9px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow whitespace-nowrap">
                        <Sparkles size={12} /> Find a Provider
                      </Link>
                      <Link to={`/app/cases/${r.id}`} className="text-[12px] font-medium text-muted-c">Open</Link>
                      <button onClick={() => setConfirmDelete(r)} className="text-[12px] font-medium text-muted-c">Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: the full case table. */}
            <div className="hidden md:block bg-elevated rounded-xl border border-theme overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px]">
                  <thead>
                    <tr className="border-b border-theme">
                      {['Client', 'Status', 'Quick links', 'Date of birth', 'Date of injury', 'Case', 'Actions'].map((h, i) => (
                        <th key={h} className={`text-[11px] font-semibold text-muted-c uppercase tracking-wider px-4 py-3 ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {filtered.map((r) => {
                      const pill = CASE_PILL[r.status] || ['bg-tertiary text-muted-c', 'New'];
                      const [typeLabel, typeCls] = TYPE_CHIP[r.case_type] || TYPE_CHIP.other;
                      const alert = r.funding?.alerts?.[0];
                      const email = displayEmail(r.person.email, '');
                      return (
                        <tr key={r.id} className="hover:bg-hover-surface transition-all">
                          <td className="px-4 py-3.5">
                            <Link to={`/app/cases/${r.id}`} className="flex items-center gap-3 group">
                              <div className="w-[34px] h-[34px] rounded-[10px] bg-accent-glow flex items-center justify-center text-[11px] font-bold text-accent-light flex-shrink-0">{r.initials}</div>
                              <span className="text-[13px] font-semibold text-primary-c group-hover:text-accent-light transition-colors whitespace-nowrap">{r.fullName}</span>
                            </Link>
                            <div className="text-[10.5px] font-mono text-muted-c mt-0.5 ml-[46px]">{r.case_number}</div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${pill[0]}`}>{pill[1]}</span>
                            {alert && (
                              <div className={`text-[10.5px] mt-1 max-w-[240px] ${alert.level === 'critical' ? 'text-danger' : 'text-warning'}`}>{alert.text}</div>
                            )}
                            {r.treatment && (r.treatment.visits_completed > 0 || r.treatment.next_visit_at) && (
                              <div className="text-[10.5px] text-muted-c mt-1 whitespace-nowrap">
                                {r.treatment.visits_completed} visit{r.treatment.visits_completed === 1 ? '' : 's'}
                                {r.treatment.days_since_last != null ? ` · last ${r.treatment.days_since_last === 0 ? 'today' : `${r.treatment.days_since_last}d ago`}` : ''}
                                {r.treatment.next_visit_at ? ` · next ${fmtD(r.treatment.next_visit_at.slice(0, 10))}` : ''}
                              </div>
                            )}
                            {r.treatment?.alerts?.[0] && (
                              <div className={`text-[10.5px] mt-1 max-w-[240px] ${r.treatment.alerts[0].level === 'critical' ? 'text-danger' : 'text-warning'}`}>{r.treatment.alerts[0].text}</div>
                            )}
                            {!r.treatment?.visits_completed && TREATMENT_LABEL[r.person.treatment_status] && (
                              <div className="text-[10.5px] text-muted-c mt-1">{TREATMENT_LABEL[r.person.treatment_status]}</div>
                            )}
                            {r.person.new_docs_7d > 0 && (
                              <div className="flex items-center gap-1 text-[10.5px] font-medium text-accent-light mt-1 whitespace-nowrap">
                                <FileText size={10} /> New document{r.person.new_docs_7d > 1 ? 's' : ''} loaded
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <Link to={findHref(r)} className="inline-flex items-center gap-1.5 px-3 py-[7px] rounded-[8px] text-[11.5px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all whitespace-nowrap">
                              <Sparkles size={12} /> Find a Provider
                            </Link>
                          </td>
                          <td className="px-4 py-3.5 text-[12.5px] text-secondary-c whitespace-nowrap">{fmtD(r.person.date_of_birth)}</td>
                          <td className="px-4 py-3.5 text-[12.5px] text-secondary-c whitespace-nowrap">{fmtD(r.accident_date)}</td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-block px-2 py-0.5 rounded-md text-[10.5px] font-bold ${typeCls}`}>{typeLabel}</span>
                            {r.funding?.current?.chip && <div className="text-[10.5px] text-secondary-c mt-1 whitespace-nowrap">{r.funding.current.chip}</div>}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex justify-end gap-2 whitespace-nowrap">
                              {email && !email.includes('@contact.local') && (
                                inviteSuccess === r.id ? (
                                  <span className="text-[12px] font-medium text-success">Sent!</span>
                                ) : (
                                  <button onClick={() => handleInvite(r)} disabled={inviting === r.id} className="text-[12px] font-medium text-accent-light hover:text-accent transition-colors disabled:opacity-50">
                                    {inviting === r.id ? 'Sending...' : 'Invite'}
                                  </button>
                                )
                              )}
                              <Link to={`/app/contacts/${r.client_id}`} className="text-[12px] font-medium text-muted-c hover:text-accent-light transition-colors">Contact</Link>
                              <button onClick={() => setConfirmDelete(r)} className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors">Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <p className="text-[11px] text-muted-c mt-3">{cases.length} case{cases.length !== 1 ? 's' : ''} total</p>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete case"
        message={`Delete case ${confirmDelete?.case_number} for ${confirmDelete?.fullName}? The contact stays; only the case and its coverage records are removed.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
